import { useNavigation, useList } from '@refinedev/core'
import { ResourceForm } from '@/components/shared/ResourceForm'
import { Button } from '@/components/ui/button'
import { toTitleCase } from '@/lib/utils'
import { ArrowLeft } from 'lucide-react'
import {
  RESOURCE_FIELD_ORDER,
  HIDDEN_FIELDS,
  SALT_FIELDS,
  resolveFieldKind,
} from '@/resources/fieldSchema'

function buildDefaultValues(fields: string[], resource: string): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  for (const k of fields) {
    if (resource === 'onboarding_services' && k === 'base_url') continue
    const kind = resolveFieldKind(resource, k)
    if (kind === 'boolean') { defaults[k] = false; continue }
    if (kind === 'array' || kind === 'array_enum') { defaults[k] = []; continue }
    defaults[k] = ''
  }
  if (resource === 'onboarding_services') {
    defaults.provider = 'PeopleMate'
  }
  return defaults
}

export function CreatePage({ resource }: { resource: string }) {
  const { goBack } = useNavigation()

  // Schema-defined fields take priority — guarantees the form renders even for empty tables
  const schemaFields = RESOURCE_FIELD_ORDER[resource]

  // Fall back to a sample-row approach only when there is no schema definition
  const needsSample = !schemaFields
  const { data } = useList({
    resource,
    pagination: { pageSize: 1 },
    queryOptions: { enabled: needsSample },
  })

  const hiddenForResource = [
    ...(HIDDEN_FIELDS[resource] ?? []),
    ...(SALT_FIELDS[resource] ?? []),
    'id', 'created_at', 'updated_at',
  ]

  let fields: string[]
  if (schemaFields) {
    fields = schemaFields.filter((f) => !hiddenForResource.includes(f))
  } else {
    const sample = (data?.data?.[0] ?? {}) as Record<string, unknown>
    fields = Object.keys(sample).filter((k) => !hiddenForResource.includes(k))
  }

  const defaultValues = buildDefaultValues(fields, resource)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => goBack()}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight break-words">Create {toTitleCase(resource)}</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{fields.length} fields</p>
        </div>
      </div>
      {fields.length === 0 && needsSample && !data ? (
        <p className="text-sm text-muted-foreground">Loading form schema…</p>
      ) : fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No fields found. Add a <code>RESOURCE_FIELD_ORDER</code> entry for{' '}
          <code>{resource}</code> in <code>fieldSchema.ts</code>.
        </p>
      ) : (
        <ResourceForm resource={resource} action="create" defaultValues={defaultValues} />
      )}
    </div>
  )
}
